import type { ReactNode } from 'react'
import { DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, useSortable, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { referenceMediaKey } from '../shared/mentionUtils'
import { AddReferenceButton } from '../shared/AddReferenceButton'
import { useTranslation } from 'react-i18next'
import { GripVertical, X } from 'lucide-react'
import type { SelectedReference } from '../shared/ReferenceSelector'
import { ReferenceThumbnailButton } from '../shared/ReferenceMediaPreview'
import { REFERENCE_SECTION_LABEL_KEY } from '../shared/referenceSectionLabel'
import { useNuxTooltipEvents } from '../../../../onboarding/nuxEvents'

interface VideoReferencePickerProps {
  nodeId: string
  references: SelectedReference[]
  projectId: string | null | undefined
  cacheKey?: string
  canSelectReferences: boolean
  onOpenSelector: (event?: { stopPropagation: () => void }) => void
  onSelectCanvas?: () => void
  onRemoveReference: (reference: SelectedReference) => void
  onReorderReferences?: (references: SelectedReference[]) => void
}

function SortableReference({ reference, disabled, children }: {
  reference: SelectedReference; disabled: boolean; children: ReactNode
}) {
  const { t } = useTranslation('canvas')
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: referenceMediaKey(reference), disabled,
  })
  return <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}
    className="relative group/ref" data-reference-key={referenceMediaKey(reference)}
    data-testid={`video-generator-${reference.mediaType}-reference-${reference.nodeId}`}>
    {children}
    {!disabled && <button type="button" {...attributes} {...listeners}
      aria-label={t('referenceSelector.reorderReference', { name: reference.label })} title={t('referenceSelector.reorderReference', { name: reference.label })}
      className="nodrag nopan absolute bottom-0 left-0 flex h-5 w-5 cursor-grab touch-none items-center justify-center rounded border border-input-border bg-surface-1 text-foreground hover:bg-surface-3 hover:border-input-border-hover focus-visible:ring-2 focus-visible:ring-primary">
      <GripVertical className="h-3 w-3" />
    </button>}
  </div>
}

export function VideoReferencePicker({
  nodeId,
  references,
  projectId,
  cacheKey,
  canSelectReferences,
  onOpenSelector,
  onRemoveReference,
  onSelectCanvas,
  onReorderReferences,
}: VideoReferencePickerProps) {
  const { t } = useTranslation('canvas')
  const { emitNuxEvent } = useNuxTooltipEvents()
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }))
  const referenceSectionLabel = t(REFERENCE_SECTION_LABEL_KEY)
  const emitReferencesChanged = () => {
    emitNuxEvent({
      type: 'controlActivated',
      controlId: 'videoReferencesChanged',
      anchorId: 'video-generator-references',
      nodeId,
      nodeType: 'videoGenerator',
    })
  }

  return (
    <>
      <div
        data-testid="video-generator-config-references"
      >
        <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1">
          {referenceSectionLabel}
        </label>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={({ active, over }) => {
          if (!over || active.id === over.id || !onReorderReferences) return
          const from = references.findIndex(ref => referenceMediaKey(ref) === active.id)
          const to = references.findIndex(ref => referenceMediaKey(ref) === over.id)
          if (from >= 0 && to >= 0) {
            onReorderReferences(arrayMove(references, from, to))
            emitReferencesChanged()
          }
        }}>
        <SortableContext items={references.map(referenceMediaKey)} strategy={rectSortingStrategy}>
        <div className="flex flex-wrap gap-1" data-testid="video-generator-reference-row">
          {references.length > 0 && (
            <div
              className="inline-flex w-fit max-w-full flex-wrap gap-1 align-top"
              data-testid="video-generator-reference-thumbnail-group"
              data-nux-anchor="video-generator-references"
              data-nux-node-id={nodeId}
              data-nux-node-type="videoGenerator"
            >
              {references.map((reference) => {
                return (
                  <SortableReference key={referenceMediaKey(reference)} reference={reference}
                    disabled={!onReorderReferences || !canSelectReferences}>
                    <ReferenceThumbnailButton
                      panelInteractions
                      audioTestId={`video-generator-audio-reference-waveform-${reference.nodeId}`}
                      reference={reference}
                      projectId={projectId}
                      cacheBust={cacheKey}
                      ownerNodeId={nodeId}
                      surface="config-reference-row"
                      className="rounded-lg border border-input-border bg-surface-1 text-text-muted transition-colors hover:border-input-border-hover hover:bg-surface-3"
                      mediaClassName="h-full w-full object-cover"
                      testId={`video-generator-${reference.mediaType}-reference-preview-${reference.nodeId}`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        onRemoveReference(reference)
                        emitReferencesChanged()
                      }}
                      className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full border border-input-border bg-destructive text-white opacity-0 transition-opacity group-hover/ref:opacity-100"
                      title={t('referenceSelector.remove')}
                      data-testid={`video-generator-remove-reference-${reference.nodeId}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </SortableReference>
                )
              })}
            </div>
          )}
          {canSelectReferences && (
            <AddReferenceButton
              onOpenSelector={() => {
                emitReferencesChanged()
                onOpenSelector()
              }}
              onSelectCanvas={onSelectCanvas ? () => {
                emitReferencesChanged()
                onSelectCanvas()
              } : undefined}
              testId="video-generator-add-reference-btn"
            />
          )}
        </div>
        </SortableContext>
        </DndContext>
      </div>
    </>
  )
}
